import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import PaymentsPanel, { PAYMENT_STATE, localToday, fmtDate, parseDate } from './PaymentsPanel';

// (E) Xizmat shartnomasi hisoblari: avans (shartnomaga) va yakuniy
// (imzolangan dalolatnomaga). Backend: app/routers/service_invoices.py -
// faqat buxgalteriya / rahbariyat (servis muhandisi ko'rmaydi, server 403/404).
// Yakuniy = dalolatnoma summasi - hali ishlatilmagan avans (FIFO); hisob-
// fakturalar yig'indisi shartnoma summasidan oshmaydi (server tekshiradi).
interface Allocation { advance_invoice_id: number; advance_number: string; amount: number; }
interface SInvoice {
  id: number; number: string; kind: 'advance' | 'final'; status: string; service_act_id?: number | null;
  act_number?: string | null; amount: number; paid_amount: number; vat_percent: number; net_amount: number;
  vat_amount: number; act_amount?: number | null; advance_applied?: number | null; allocations: Allocation[];
  paid?: string; outstanding?: string; payment_state?: string | null;
  due_date?: string | null; overdue?: boolean; e_invoice_number?: string | null; e_invoice_date?: string | null;
}
interface SignedAct { id: number; number: string; signed_date?: string | null; total_amount: number; final_invoice_id?: number | null; }
interface Summary {
  contract_total: number; advance_total: number; advance_used: number; advance_unused: number;
  final_total: number; invoiced_total: number; cap_left: number;
}

const fmt = (n?: number | null) => (n ?? 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Qoralama', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Berilgan', color: 'bg-blue-100 text-blue-700' },
  paid: { label: "To'langan", color: 'bg-green-100 text-green-700' },
  overdue: { label: "Muddati o'tgan", color: 'bg-orange-100 text-orange-700' },
  cancelled: { label: 'Bekor qilingan', color: 'bg-red-100 text-red-700' },
};

const ServiceInvoicesPanel: React.FC<{ contractId: number; canFull: boolean; clientId?: number | null; currency?: string }> = ({ contractId, canFull, clientId, currency }) => {
  const [invoices, setInvoices] = useState<SInvoice[]>([]);
  const [acts, setActs] = useState<SignedAct[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [lang, setLang] = useState('uz');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/service-invoices', { params: { contract_id: contractId } });
      setInvoices(r.data.invoices); setActs(r.data.signed_acts); setSummary(r.data.summary);
    } catch { setInvoices([]); setActs([]); setSummary(null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [contractId]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setBusy(false); }
  };

  const createAdvance = () => run(async () => {
    const amount = advanceAmount.trim() ? Number(advanceAmount) : null;
    await api.post(`/service-invoices/advance/${contractId}`, amount ? { amount } : {});
    setAdvanceAmount('');
  });
  const createFinal = (actId: number) => run(() => api.post(`/service-invoices/final/${actId}`));
  const setStatus = (inv: SInvoice, status: 'sent' | 'cancelled') => {
    if (status === 'cancelled' && !window.confirm(`${inv.number} hisobini bekor qilasizmi? (raqam saqlanadi)`)) return;
    run(() => api.put(`/service-invoices/${inv.id}/status`, { status }));
  };
  // To'lov muddati avtomatik (ish kalendari: shanba/yakshanba, bayram va ko'chirilgan kunlar) - buxgalter to'g'rilaydi.
  const editDue = (inv: SInvoice) => {
    const dt = window.prompt("To'lov muddati (dd.mm.yyyy):", fmtDate(inv.due_date));
    if (!dt || !dt.trim()) return;
    const iso = parseDate(dt);
    if (!iso) { window.alert("Sana dd.mm.yyyy ko'rinishida bo'lsin"); return; }
    run(() => api.put(`/service-invoices/${inv.id}`, { due_date: iso }));
  };
  // Rasmiy e-hisob-faktura tizimdan tashqarida chiqariladi - raqami/sanasi shu yerda qayd etiladi.
  const editEInvoice = (inv: SInvoice) => {
    const num = window.prompt("E-hisob-faktura raqami (bo'sh - o'chirish):", inv.e_invoice_number || '');
    if (num === null) return;
    const dt = num.trim() ? window.prompt('E-hisob-faktura sanasi (dd.mm.yyyy):', fmtDate(inv.e_invoice_date)) : '';
    if (dt === null) return;
    const iso = dt.trim() ? parseDate(dt) : null;
    if (dt.trim() && !iso) { window.alert("Sana dd.mm.yyyy ko'rinishida bo'lsin"); return; }
    run(() => api.put(`/service-invoices/${inv.id}`, { e_invoice_number: num.trim() || null, e_invoice_date: iso }));
  };
  // Hisob-kitob holati - tanlangan sanaga (standart: bugun): shu sanagacha hisoblar va to'lovlar.
  const [asOf, setAsOf] = useState(localToday());
  const downloadStatement = async () => {
    const r = await api.get('/payments/statement/pdf', { params: { contract_id: contractId, lang, as_of: asOf || undefined }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `hisob-kitob_${contractId}_${asOf}_${lang}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const download = async (inv: SInvoice) => {
    const r = await api.get(`/service-invoices/${inv.id}/export/pdf`, { params: { lang }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${inv.number}_${lang}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!summary) return null;
  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h4 className="font-semibold text-gray-700 text-sm">🧾 To&apos;lov uchun hisoblar (avans / yakuniy)</h4>
        <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} title="Hisob-kitob holati sanasi"
          className="border rounded px-1 py-0.5 text-xs" />
        <button onClick={downloadStatement} className="px-2 py-1 border rounded text-xs hover:bg-gray-50">Hisob-kitob holati (PDF)</button>
        <select value={lang} onChange={e => setLang(e.target.value)} className="border rounded px-2 py-1 text-xs">
          <option value="uz">PDF: uz</option><option value="ru">PDF: ru</option><option value="en">PDF: en</option>
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
        <div className="bg-gray-50 rounded p-2">Shartnoma: <b>{fmt(summary.contract_total)}</b></div>
        <div className="bg-gray-50 rounded p-2">Avans: <b>{fmt(summary.advance_total)}</b> (ishlatilmagan {fmt(summary.advance_unused)})</div>
        <div className="bg-gray-50 rounded p-2">Hisoblar: <b>{fmt(summary.invoiced_total)}</b></div>
        <div className="bg-gray-50 rounded p-2">Qoldiq (chegara): <b>{fmt(summary.cap_left)}</b></div>
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

      {canFull && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} type="number" min={0} step="0.01"
            placeholder="Avans summasi (bo'sh = 2.2-band foizi)" className="border rounded px-2 py-1 text-xs w-64" />
          <button disabled={busy} onClick={createAdvance}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">+ Avans hisobi</button>
        </div>
      )}

      {acts.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1">Imzolangan dalolatnomalar - yakuniy hisob (dalolatnoma summasi − ishlatilmagan avans, eng eski avansdan):</p>
          <div className="space-y-1">
            {acts.map(a => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">{a.number}</span>
                <span className="text-gray-500">{fmtDate(a.signed_date)}</span>
                <span>{fmt(a.total_amount)}</span>
                {a.final_invoice_id
                  ? <span className="text-green-700">✓ yakuniy hisob bor</span>
                  : canFull && <button disabled={busy} onClick={() => createFinal(a.id)}
                      className="px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">+ Yakuniy hisob</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-2">Raqam</th><th className="pr-2">Tur</th><th className="pr-2 text-right">Summa</th>
              <th className="pr-2 text-right">QQS</th><th className="pr-2 text-right">To&apos;langan / qoldiq</th><th className="pr-2">Holat</th><th className="pr-2">Muddat</th><th></th>
            </tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b align-top">
                  <td className="py-1 pr-2 font-medium whitespace-nowrap">{inv.number}</td>
                  <td className="pr-2">
                    {inv.kind === 'advance' ? 'Avans' : `Yakuniy (${inv.act_number || ''})`}
                    {inv.kind === 'final' && (inv.advance_applied ?? 0) > 0 && (
                      <div className="text-gray-500">
                        {fmt(inv.act_amount)} − avans {inv.allocations.map(x => `${x.advance_number}: ${fmt(x.amount)}`).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="pr-2 text-right">{fmt(inv.amount)}</td>
                  <td className="pr-2 text-right">{inv.vat_percent ? `${inv.vat_percent}%: ${fmt(inv.vat_amount)}` : 'QQSsiz'}</td>
                  <td className="pr-2 text-right">{inv.payment_state ? `${fmt(Number(inv.paid))} / ${fmt(Number(inv.outstanding))}` : '—'}</td>
                  <td className="pr-2">
                    {inv.payment_state
                      ? <span className={`px-2 py-0.5 rounded ${PAYMENT_STATE[inv.payment_state]?.color || ''}`}>{PAYMENT_STATE[inv.payment_state]?.label}</span>
                      : <span className={`px-2 py-0.5 rounded ${STATUS[inv.status]?.color || ''}`}>{STATUS[inv.status]?.label || inv.status}</span>}
                  </td>
                  <td className="pr-2 whitespace-nowrap">
                    {fmtDate(inv.due_date)}
                    {inv.overdue && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700">muddati o&apos;tgan</span>}
                    {canFull && inv.status !== 'cancelled' && (
                      <button onClick={() => editDue(inv)} title="Avtomatik hisoblangan (ish kunlari - Buxgalteriya → Ish kalendari) - tahrirlash"
                        className="ml-1 text-blue-600 hover:underline">✎</button>
                    )}
                  </td>
                  <td className="whitespace-nowrap space-x-1">
                    <button onClick={() => download(inv)} className="px-2 py-0.5 border rounded hover:bg-gray-50">PDF</button>
                    {canFull && (
                      <button onClick={() => editEInvoice(inv)} title="Tashqi e-hisob-faktura raqami va sanasi"
                        className="px-2 py-0.5 border rounded hover:bg-gray-50">E-HF{inv.e_invoice_number ? `: ${inv.e_invoice_number}` : ''}</button>
                    )}
                    {canFull && inv.status === 'draft' && (
                      <button disabled={busy} onClick={() => setStatus(inv, 'sent')} className="px-2 py-0.5 border rounded text-blue-700 hover:bg-blue-50">Berish</button>
                    )}
                    {canFull && inv.status !== 'cancelled' && (
                      <button disabled={busy} onClick={() => setStatus(inv, 'cancelled')} className="px-2 py-0.5 border rounded text-red-700 hover:bg-red-50">Bekor</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {clientId && <PaymentsPanel clientId={clientId} contractId={contractId} currency={currency || 'uzs'} canFull={canFull} onChange={load} />}
    </div>
  );
};

export default ServiceInvoicesPanel;
